import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface QAItem {
  question: string;
  answer: string;
}

interface UpdateQAParams {
  productId: string;
  qaItems: QAItem[];
}

interface GenerateQAParams {
  productSlug: string;
}

export const useProductQA = () => {
  const queryClient = useQueryClient();

  const updateQA = useMutation({
    mutationFn: async ({ productId, qaItems }: UpdateQAParams) => {
      // First, get the product's database ID
      const { data: product } = await supabase
        .from('products')
        .select('id')
        .eq('slug', productId)
        .single();

      if (!product) {
        throw new Error('Product not found');
      }

      // Delete all existing Q&A for this product
      await supabase
        .from('product_qa')
        .delete()
        .eq('product_id', product.id);

      // Insert new Q&A items
      if (qaItems.length > 0) {
        const qaData = qaItems.map((item, index) => ({
          product_id: product.id,
          question: item.question,
          answer: item.answer,
          sort_order: index,
        }));

        const { error } = await supabase.from('product_qa').insert(qaData);

        if (error) throw error;
      }

      return { success: true };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['product', variables.productId] });
      toast.success('Q&A updated successfully');
    },
    onError: (error: any) => {
      console.error('Error updating Q&A:', error);
      toast.error('Failed to update Q&A: ' + error.message);
    },
  });

  const generateQA = useMutation({
    mutationFn: async ({ productSlug }: GenerateQAParams) => {
      const { data, error } = await supabase.functions.invoke('generate-product-qa', {
        body: { productSlug }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['product', variables.productSlug] });
      toast.success('Q&A generated successfully with AI');
    },
    onError: (error: any) => {
      console.error('Error generating Q&A:', error);
      toast.error('Failed to generate Q&A: ' + error.message);
    },
  });

  return {
    updateQA: updateQA.mutate,
    isUpdating: updateQA.isPending,
    generateQA: generateQA.mutate,
    isGenerating: generateQA.isPending,
  };
};
